import React, { CSSProperties, isValidElement } from 'react';

import { GridColumn, GridColumnCheckbox, GridColumnSwitch } from './columns/GridColumn';
import { ColumnElement } from './columns/types';
import { HeaderCSS } from './header/types';

export const filterHideColumns = (children: React.ReactNode): ColumnElement[] => {
  const columns = React.Children.toArray(children) as ColumnElement[];
  return columns
    .filter((column) => column.props && !column.props.hide)
    .map((column) => {
      const { children } = column.props;
      const arr = React.Children.toArray(children) as ColumnElement[];
      const is_column = arr.some((child) => isColumnElement(child));

      if (arr.length > 0 && is_column) {
        const children_filtered = filterHideColumns(children);
        if (children_filtered.length === 0) {
          return null;
        }
        return React.cloneElement(column, { ...column.props, children: children_filtered });
      }
      return column;
    })
    .filter(Boolean) as ColumnElement[];
};

export const calculateDepth = (children: ColumnElement | ColumnElement[]): number => {
  let max_depth = 1;

  const traverseChildren = (element: ColumnElement, current_depth: number) => {
    if (React.isValidElement(element) && isColumnElement(element)) {
      max_depth = Math.max(max_depth, current_depth);
      React.Children.forEach(element.props.children, (child) => {
        if (!!child && isColumnElement(element)) {
          traverseChildren(child, current_depth + 1);
        }
      });
    }
  };

  React.Children.forEach(children, (child) => {
    if (isColumnElement(child)) {
      traverseChildren(child, 1);
    }
  });

  return max_depth;
};

export const isValidReactElement = (element: React.ReactNode): element is React.ReactElement => {
  return isValidElement(element);
};

export const isColumnElement = (element: React.ReactNode): element is ColumnElement => {
  return (
    React.isValidElement(element) &&
    (element.type === GridColumn ||
      element.type === GridColumnCheckbox ||
      element.type === GridColumnSwitch)
  );
};

export const cellCSS = ({
  height,
  depth = 1,
  fixed,
  width,
  filler_required,
  cumulative_fixed_right_widths = [],
  cumulative_fixed_left_widths = [],
  cumulative_column_widths = [],
  index,
}: HeaderCSS): CSSProperties => {
  const right = fixed === 'right';
  const left = fixed === 'left';

  const fixed_left =
    left && cumulative_fixed_left_widths[index] === cumulative_fixed_left_widths[index + 1];

  return {
    position: fixed ? (filler_required ? 'absolute' : 'sticky') : 'absolute',
    left: right
      ? `calc(100% - ${cumulative_fixed_right_widths[index]}px)`
      : `${cumulative_column_widths[index - 1] || 0}px`,
    width: `${width}px`,
    height: `${height * depth}px`,
    borderRight: fixed_left ? '1px solid pink' : undefined,
    zIndex: fixed ? 2 : 1,
  };
};

export const sortStrings = (a: string, b: string) =>
  a.localeCompare(b, 'en-US', {
    numeric: true,
    sensitivity: 'base',
  });

export const sortNumbers = (a: number, b: number) => a - b;

export const sortDates = (a: Date, b: Date) => a.getTime() - b.getTime();

export const flattenChildrenColumns = (columns: ColumnElement[]): ColumnElement[] => {
  const flatten_columns: ColumnElement[] = [];

  const flattenColumns = (cols: ColumnElement[]): void => {
    cols.forEach((col) => {
      if (isColumnElement(col)) {
        const children = React.Children.toArray(col.props.children || []).filter(isColumnElement);
        if (children.length) {
          flattenColumns(children as ColumnElement[]);
        } else {
          flatten_columns.push(col);
        }
      }
    });
  };

  flattenColumns(columns);
  return flatten_columns;
};

export const reconstructChildren = (
  cols: ColumnElement[],
  flatten_columns: ColumnElement[]
): ColumnElement[] => {
  let flat_index = 0;

  const reconstructColumns = (columns: ColumnElement[]): ColumnElement[] => {
    return columns.map((col) => {
      const children = React.Children.toArray(col.props.children || []).filter(isColumnElement);
      if (isColumnElement(col) && children.length) {
        return React.cloneElement(col, {
          ...col.props,
          children: reconstructColumns(children as ColumnElement[]),
        });
      } else {
        return flatten_columns[flat_index++];
      }
    });
  };

  return reconstructColumns(cols);
};
